// src/App.tsx
import { BrowserRouter, Routes, Route, Outlet } from "react-router-dom";
import Landing from "./pages/Landing";
import Profile from "./pages/Profile";
import Dashboard from "./pages/Dashboard";
import Trips from "./pages/Trips";

import TripDetail from "./pages/TripDetail";
import EditTrip from "./pages/EditTrip";
import { AuthProvider } from "./contexts/AuthContext";
import { UserProfileProvider } from "./contexts/UserProfileContext";
import ProtectedRoute from "./components/ProtectedRoute";
import JoinTripPage from "./pages/JoinTrip";
import TripView from "./pages/TripView";
import CreateBlogPost from "./pages/CreateBlogPost";
import EditBlogPost from "./pages/EditBlogPost";
import ResetPassword from "./pages/ResetPassword";
import AdminPanel from "./pages/admin/AdminPanel";
import PacklistCategoriesAdmin from "./pages/admin/PacklistCategoriesAdmin";
import PacklistTagsAdmin from "./pages/admin/PacklistTagsAdmin";
import AdminPacklistOverview from "./pages/admin/AdminPacklistOverview";
import PacklistTemplatesAdmin from "./pages/admin/PacklistTemplatesAdmin";
import AdminPushDashboard from "./pages/admin/AdminPushDashboard";
import AdminUserOverview from "./pages/admin/AdminUserOverview";
import AdminUserDetail from "./pages/admin/AdminUserDetail";
import AdminUserFeatures from "./pages/admin/AdminUserFeatures";
import PushOverviewPage from "./pages/admin/PushOverviewPage";
import NotificationPage from "./pages/NotificationPage";

import PacklistTemplateItemsAdmin from "./pages/admin/PacklistTemplateItemsAdmin";
import PacklistItemLibraryAdmin from "./pages/admin/PacklistItemLibraryAdmin";
import { ConfirmDialogProvider } from "./contexts/ConfirmDialogContext";
import { useEffect } from "react";
import { useAuth } from "./contexts/AuthContext";
import { usePushNotifications } from "./lib/usePushNotifications";
import GlobalHeaderButtons from "./components/GlobalHeaderButtons";
import BottomTabBar from "./components/BottomTabBar";
import ChatOverview from "./pages/ChatOverview";
import TripChat from "./pages/TripChat";
import PollAnswerPage from "./pages/PollAnswerPage";

import DisclaimerGate from "./components/DisclaimerGate";
import FeedbackList from "./pages/FeedbackList";
import FeedbackThread from "./pages/FeedbackThread";
import FAQList from "./pages/FAQList";
import FAQEntry from "./pages/FAQEntry";
import AdminFAQList from "./pages/admin/AdminFAQList";
import AdminFAQEdit from "./pages/admin/AdminFAQEdit";
import RechtlichesUndDatenschutz from "./pages/legal/RechtlichesUndDatenschutz";
import CookieConsent from "./components/CookieConsent";

import AdminGate from "./components/AdminGate";
import ErrorBoundary from "./components/ErrorBoundary";
import OfflineIndicator from "./components/OfflineIndicator";
import AdminKpiPage from "./pages/admin/AdminKpiPage";
import AdminEmails from "./pages/admin/AdminEmails";
import AdminEmailTemplates from "./pages/admin/emails/AdminEmailTemplates";
import AdminEmailRules from "./pages/admin/emails/AdminEmailRules";
import AdminEmailQueue from "./pages/admin/emails/AdminEmailQueue";
import AdminEmailSends from "./pages/admin/emails/AdminEmailSends";
import AdminEmailTestBoard from "./pages/admin/emails/AdminEmailTestBoard";

import { useLocation, useNavigate } from "react-router-dom";
import { initAnalyticsFromStoredConsent, trackPageview } from "./lib/analytics";


function RouteAnalytics() {
  const location = useLocation();

  // Beim Mount: bereits erteilte Zustimmung berücksichtigen
  useEffect(() => {
    initAnalyticsFromStoredConsent();
  }, []);

  // Bei Routenwechsel Pageview senden (nur aktiv, wenn zugestimmt wurde)
  useEffect(() => {
    trackPageview(location.pathname + location.search);
  }, [location.pathname, location.search]);

  // Wenn Nutzer seine Wahl ändert → GA (de)aktivieren & aktuellen Pageview senden
  useEffect(() => {
    const onConsentChange = () => {
      initAnalyticsFromStoredConsent();
      trackPageview(location.pathname + location.search);
    };
    window.addEventListener("cookie-consent:change", onConsentChange as EventListener);
    return () => window.removeEventListener("cookie-consent:change", onConsentChange as EventListener);
  }, [location.pathname, location.search]);

  return null;
}



function PasswordRecoveryRedirect() {
  const { isPasswordRecovery } = useAuth();
  const navigate = useNavigate();
  useEffect(() => {
    if (isPasswordRecovery) navigate("/reset-password", { replace: true });
  }, [isPasswordRecovery, navigate]);
  return null;
}

export default function App() {
  return (
    <AuthProvider>
      <CookieConsent /> 
     
      <UserProfileProvider>
        <ConfirmDialogProvider>
          <BrowserRouter>
            <OfflineIndicator />
            <GlobalHeaderButtons />
            <BottomTabBar />
            <RouteAnalytics />
            <PasswordRecoveryRedirect />
            <DisclaimerGate />
            <ErrorBoundary>
            <Routes>
              <Route path="/" element={<Landing />} />
              <Route path="/feedback" element={<FeedbackList />} />
              <Route path="/feedback/:threadId" element={<FeedbackThread />} />
              <Route path="/faq" element={<FAQList />} />
              <Route path="/faq/:slug" element={<FAQEntry />} />
              
              <Route path="/rechtliches" element={<RechtlichesUndDatenschutz />} />
              <Route
                path="/profile"
                element={
                  <ProtectedRoute>
                    <Profile />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/dashboard"
                element={
                  <ProtectedRoute>
                    <Dashboard />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/trips"
                element={
                  <ProtectedRoute>
                    <Trips />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/trip/:id"
                element={
                  <ProtectedRoute>
                    <TripDetail />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/trip/:id/edit"
                element={
                  <ProtectedRoute>
                    <EditTrip />
                  </ProtectedRoute>
                }
              />
              <Route path="/join/:joinCode" element={<JoinTripPage />} />
              <Route
                path="/trip/:id/view"
                element={
                  <ProtectedRoute>
                    <TripView />
                  </ProtectedRoute>
                }
              />
              <Route path="/create-blog" element={<CreateBlogPost />} />
              <Route path="/edit-blog/:id" element={<EditBlogPost />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              
              <Route path="/notifications" element={<NotificationPage />} />
              <Route path="/chats" element={<ChatOverview />} />
              <Route path="/chat/:tripId" element={<TripChat />} />
              <Route
                path="/trip/:tripId/poll/:programItemId"
                element={<PollAnswerPage />}
              />

              {/* Admin-Sektion: alles darunter ist per Gate geschützt */}
              <Route element={<AdminGate><Outlet /></AdminGate>}>
                <Route path="/admin" element={<AdminPanel />} />
                <Route path="/admin/kpis" element={<AdminKpiPage />} />
                <Route path="/admin/faq" element={<AdminFAQList />} />
                <Route path="/admin/faq/:id" element={<AdminFAQEdit />} />
                <Route path="/admin" element={<AdminPanel />} />
                <Route
                  path="/admin/packlists"
                  element={<AdminPacklistOverview />}
                />
                <Route
                  path="/admin/packlists/categories"
                  element={<PacklistCategoriesAdmin />}
                />
                <Route
                  path="/admin/packlists/tags"
                  element={<PacklistTagsAdmin />}
                />
                <Route
                  path="/admin/packlists/templates"
                  element={<PacklistTemplatesAdmin />}
                />
                <Route
                  path="/admin/packlists/templates/:id/items"
                  element={<PacklistTemplateItemsAdmin />}
                />
                <Route
                  path="/admin/packlist-items"
                  element={<PacklistItemLibraryAdmin />}
                />
                <Route path="/admin/push" element={<AdminPushDashboard />} />
                <Route path="/admin/users" element={<AdminUserOverview />} />
                <Route
                  path="/admin/users/:userId"
                  element={<AdminUserDetail />}
                />
                <Route
                  path="/admin/users/:userId/push"
                  element={<PushOverviewPage />}
                />
                <Route
                  path="/admin/users/:userId/features"
                  element={<AdminUserFeatures />}
                />
                <Route path="/admin/emails" element={<AdminEmails />} />
                <Route path="/admin/emails/templates" element={<AdminEmailTemplates />} />
                <Route path="/admin/emails/rules" element={<AdminEmailRules />} />
                <Route path="/admin/emails/queue" element={<AdminEmailQueue />} />
                <Route path="/admin/emails/sends" element={<AdminEmailSends />} />
                <Route path="/admin/emails/test" element={<AdminEmailTestBoard />} /> 
              </Route>


            </Routes>
            </ErrorBoundary>
          </BrowserRouter>
        </ConfirmDialogProvider>
      </UserProfileProvider>
    </AuthProvider>
  );
}
